// ============================================================
// BOOKING ENGINE — moved out of legacy/App.jsx (Phase 3, incremental split).
//
// Pure court-booking logic: validation (overlap + gap/dead-gap + optional
// smart allocation), booking creation, price-zone pricing, and slot-aggregated
// availability. Plus the booking-specific config defaults and the Norwegian/
// Swedish/Danish holiday tables the pricing rules depend on.
//
// No storage, no React. Time helpers come from ../lib/date so they stay shared
// with the other engines (banepad / seriespill). This module must NEVER import
// from legacy/App.jsx.
//
// The behavior is pinned by tests/booking.test.ts (characterization / golden
// master) — keep it byte-for-byte equivalent. Several non-obvious rules are
// load-bearing (HANDOVER §13): a direct overlap is ALWAYS blocked regardless of
// options; start/end must land on the full/half hour; a zone with no timeRanges
// covers the whole day; cfg (5th arg) controls the default price.
// ============================================================

import { toMinHHMM, fromMinHHMM } from './time.js';

// bufferMin = minimum gap (minutes) required between two bookings on the same
// court. Default 60.
export const BOOKING_DEFAULT_BUFFER_MIN = 60;
export const BOOKING_DEFAULT_DURATION_MIN = 90;

// Default configuration for the booking module — admin can override everything
// via the settings modal. Persisted as 'booking_config_v1' in Storage.
export const BOOKING_DEFAULT_CONFIG: any = {
  // Buffer: smallest allowed gap between two bookings (besides 0 = back-to-back).
  // 0 = no buffer rule, all gaps allowed including 1 min.
  // 60 = recommended: a gap must be either 0 (back-to-back) or at least 61 min.
  bufferMin: 60,
  // Which durations customers may pick. Admin mode can additionally pick any
  // 30-min multiple (30, 45, 60, 90, 120, 150, 180).
  allowedDurations: [60, 90, 120],
  // Center opening and closing time (HH:MM).
  openTime: '07:00',
  closeTime: '23:00',
  // Which courts can be booked? Empty = all (1..totalCourts).
  // A list of court numbers that are active. Used to temporarily close courts
  // for maintenance etc.
  enabledCourts: [],
  // Time step for start times (in minutes). 30 = every half hour.
  stepMin: 30,
  // Smart booking — OPTIONAL rule ON TOP of the gap rule (which always applies).
  // When on (default): limits which courts the user may pick. If the booking
  // fits more tightly on another free court (leaving less unused time), the
  // worse courts are LOCKED. The user is steered toward the court that packs the
  // calendar best. When off: the user may freely pick any gap-legal court.
  // (The gap rule — no dead gaps — applies regardless of this setting.)
  smartAllocation: true,
  // Allow the user to pick a court and book directly from the calendar matrix
  // (click on a free time). When off: the matrix is view-only, booking happens
  // via the "slot" view or admin.
  allowDirectBooking: true,
  // Time window the availability color indicator (day quick-buttons) is based
  // on. For a 24/7 center night hours inflate the "free" percentage
  // artificially, so admin can limit the calculation to "interesting" hours.
  // null = use opening time (openTime/closeTime).
  availabilityFromTime: null, // e.g. '08:00'
  availabilityToTime: null, //   e.g. '23:00'
  // How many days ahead regular users may book (incl. today). Admin mode
  // overrides and can book unlimited ahead. Default: 7 days.
  bookingWindowDays: 7,
  // Number of courts — can be set independently of the center's physical count
  // (clubInfo). null = use clubInfo.courts. Otherwise this number.
  courtCount: null,
  // Per-court metadata: nickname + optional sponsor (name + logo URL/dataURL) +
  // facility overrides. Key = court number (1-indexed).
  // { nickname, sponsorName, sponsorLogo, courtType, runoff, streaming }
  // Facility fields that are undefined inherit from courtDefaults (global).
  courtMeta: {},
  // Global default facilities for all courts (can be overridden per court).
  //   courtType: 'double' | 'single'  (doubles or singles court)
  //   runoff: bool                    (possible to run off the court)
  //   streaming: bool                 (streaming equipment installed)
  courtDefaults: { courtType: 'double', runoff: false, streaming: false },
  // Holidays: which country's set is used ('no'|'se'|'dk'), plus custom
  // additions/removals (ISO dates). Used by both price zones and recurring
  // (avoid holidays).
  holidayCountry: 'no',
  holidayExtra: [], // extra dates admin has added
  holidayRemoved: [], // dates admin has removed from the base set
  // Default/base price per hour (kr) — used for time not covered by any price
  // zone. 0 = free outside the zones.
  defaultPricePerHour: 0,
};

/**
 * How many days ahead a REGULAR user may book, counting today (#234).
 *
 * One function, because there are three callers that must agree to the day:
 * the club booking grid, the member app's date strip, and the server's
 * eligibility check on `POST /member/booking`. They did not agree before —
 * the grid honoured the setting while the member app hardcoded fourteen days
 * and the server reproduced that hardcoded number on purpose, so a club could
 * set the window to 7 and watch members book 14 days out anyway. Any rule that
 * lives in three places is a rule that will differ in three places.
 *
 * ADMIN MODE IS NOT THIS FUNCTION'S BUSINESS: admin books without limit, and
 * that is decided at the call site by not asking.
 *
 * A value that is not a usable window — missing, not a number, zero, negative
 * — falls back to the default rather than being clamped to 1. Zero days is not
 * a booking system, so it is far more likely to be an empty field or a bad
 * write than an intent to close bookings entirely; closing them has its own
 * setting.
 */
export function resolveBookingWindowDays(config?: { bookingWindowDays?: unknown } | null): number {
  const raw = Number(config?.bookingWindowDays);
  if (!Number.isFinite(raw)) return BOOKING_DEFAULT_CONFIG.bookingWindowDays;
  const whole = Math.floor(raw);
  return whole >= 1 ? whole : BOOKING_DEFAULT_CONFIG.bookingWindowDays;
}

/**
 * WHICH COURTS THIS CLUB ACTUALLY HAS.
 *
 * One function, because six callers must agree and did not. The rule was
 * written out by hand in `BookingView`, `BookingEditModal`, `MobileBook`,
 * `useBookingActions` (four times) and the Banepad views, and only one of them
 * consulted `courtCount`. The rest fell back to the CENTRE's physical count —
 * `clubInfo.courts` — so a club that has configured four courts at a five-court
 * centre got a fifth court that does not exist.
 *
 * THAT IS NOT A COSMETIC EXTRA. An unused court number has no bookings, so
 * smart booking scores it as the tightest possible placement and steers every
 * real court towards it: «Bane 2 er ledig, men ville etterlate unødvendig mye
 * tomtid her. Bane 4 passer tettere», with Bane 5 offered as free — reported
 * from a club whose calendar shows four courts. On the slot list the same
 * phantom emptied most of the day, and on the write path it decides whether a
 * booking is accepted at all.
 *
 * THE ORDER IS THE RULE:
 *   1. an explicit `enabledCourts` list wins — that is the admin closing courts;
 *   2. otherwise `courtCount`, which is the club's own configured number;
 *   3. otherwise the centre's physical count, which is the honest last resort.
 *
 * Returns court NUMBERS, 1-indexed, which is what every consumer and the engine
 * itself speak.
 */
export function resolveEffectiveCourts(
  config: { enabledCourts?: unknown; courtCount?: unknown } | null | undefined,
  physicalCourts: unknown,
): number[] {
  const enabled = config?.enabledCourts;
  if (Array.isArray(enabled) && enabled.length > 0) {
    return enabled.filter((c): c is number => Number.isFinite(c));
  }
  const configured = Number(config?.courtCount);
  const physical = Number(physicalCourts);
  const count = Number.isFinite(configured) && configured > 0 ? configured : physical;
  if (!Number.isFinite(count) || count <= 0) return [];
  return Array.from({ length: Math.floor(count) }, (_, i) => i + 1);
}

// ─────────────────────────────────────────────────────────────────────
// THE PHYSICAL COUNT TO HAND IT, for every caller — browser AND server (#723,
// #873).
//
// `resolveEffectiveCourts` is the rule; what it cannot know is which physical
// count to use when the centre has never stored one. The booking calendar's
// answer is the definition: `clubInfo.courts`, or four. It lived in the web
// app (`apps/web/src/lib/clubCourts.ts`, #862) while the API kept its own
// `enabledCourts.length || clubInfo.courts || 4` — which skipped `courtCount`,
// so a four-court club at a five-court centre was validated against a fifth
// court on the server and four in the browser (#873). One function here, so
// both sides import the same answer instead of mirroring it.
//
// This supplies an INPUT to the rule above; it does not change the rule.
// ─────────────────────────────────────────────────────────────────────

/** The physical court count assumed when the centre has stored none. */
export const DEFAULT_PHYSICAL_COURTS = 4;

/** The centre's physical court count: `clubInfo.courts`, or 4. */
export function clubPhysicalCourts(clubInfo: { courts?: unknown } | null | undefined): number {
  return Number(clubInfo?.courts) || DEFAULT_PHYSICAL_COURTS;
}

/** The court NUMBERS this club books on — the calendar's set, for every caller. */
export function clubEffectiveCourts(
  bookingConfig: { enabledCourts?: unknown; courtCount?: unknown } | null | undefined,
  clubInfo: { courts?: unknown } | null | undefined,
): number[] {
  return resolveEffectiveCourts(bookingConfig, clubPhysicalCourts(clubInfo));
}

// Can a gap (minutes) be fully filled by the allowed durations, so that no
// "indivisible" unused time is left behind? A gap counts as divisible if it is
// 0, exactly equals an allowed duration, or can be assembled from them.
//
// EVERY ALLOWED DURATION COUNTS — PRODUCT CHANGE, decided by the owner
// 2026-10-01.
//
// This used to drop everything under 90 before searching:
//
//     const cleanUnits = allowedDurations.filter((d) => d >= 90);
//
// and the comment said so plainly — «can be assembled from "clean" durations
// (>= 90 — typically 90/120). Example: 150 (2h30) can NOT be built from 90/120».
// That example is the bug: a club offering 60/90/120 was told a 150-minute gap
// could not be filled, when 60 + 90 fills it exactly, with two durations the
// club offers and that this function was handed.
//
// The name and the behaviour disagreed. A caller asking «can the allowed
// durations fill this gap» got an answer about a subset of them, chosen here
// and documented nowhere else — and on screen it became a free court that could
// not be booked, and a whole afternoon missing from «Ledige tider».
//
// THE RULE IS NOT WEAKENED: a gap must still be assembled EXACTLY. 100 minutes
// from {60, 90, 120} is still indivisible and still blocked; 150 is not.
//
// AND IT IS NOT TIGHTENED EITHER, which took a second pass to get right. The
// old line did TWO things: it chose which durations assemble, and — through
// `cleanUnits.length === 0` — it switched the whole rule OFF for a club with no
// duration of 90 or more. Letting every duration assemble made that second
// condition unreachable, so a club offering only 60 went from having no rule at
// all to a strict one, where a 210-minute gap is suddenly illegal. Nobody asked
// for that, and it is the same defect wearing different clothes: times
// disappearing from «Ledige tider» for a club shape we were not looking at. The
// engine suite caught it.
//
// So the switch stays exactly as it was, and only the assembly changed.
export function gapFillableByDurations(
  gap: number,
  allowedDurations: number[] | null | undefined,
): boolean {
  if (!allowedDurations || allowedDurations.length === 0) return true;
  if (gap <= 0) return true;
  if (allowedDurations.includes(gap)) return true;
  // The ON/OFF switch, untouched: no long duration → no divisibility rule.
  if (!allowedDurations.some((d) => d >= 90)) return true;
  // The assembly, which is what changed: every allowed duration counts.
  const units = allowedDurations.filter((d) => d > 0);
  const reach = new Array(gap + 1).fill(false);
  reach[0] = true;
  for (let i = 1; i <= gap; i++) {
    for (const d of units) {
      if (i - d >= 0 && reach[i - d]) {
        reach[i] = true;
        break;
      }
    }
  }
  return reach[gap];
}

// minBookingMin = shortest allowed booking. A gap smaller than this is "dead"
//   (can never be filled) and is always blocked. A gap >= minBookingMin is
//   "usable" (a short booking can fill it) and is allowed by the gap rule —
//   smart booking then decides whether another court is a better choice. If
//   minBookingMin is not set, we fall back to bufferMin (old behavior).
export function validateBooking(
  candidate: any,
  existingBookings: any[] = [],
  {
    bufferMin = BOOKING_DEFAULT_BUFFER_MIN,
    minBookingMin = null,
    smartAllocation = true,
    totalCourts = null,
    enabledCourts = null,
    allowedDurations = null,
    excludeCourts = null,
    // ── THE CENTRE'S OPENING HOURS ───────────────────────────────────
    //
    // OPTIONAL, and absent means UNCHECKED — which is what every caller got
    // before this existed. `findFreeSlots` has always enforced the hours
    // (`t + durationMin <= closeMin`), so a booking reached through «Ledige
    // tider» could never overrun; one reached through the club portal's «Ny
    // booking» went through here, and here had no idea what a closing time
    // was. A centre closing at 19:00 accepted 17:30–19:30.
    //
    // Passing them is therefore how a CALLER asks for the rule, and the one
    // caller that deliberately does not is the admin override — see
    // `bookingValidationOptions`. An admin may place a booking outside the
    // hours; they are told what they are overriding, which is a different
    // thing from the rule not existing.
    openTime = null,
    closeTime = null,
  }: any = {},
): any {
  // Dead-gap limit: a gap must be 0 or at least this value. We take the
  // STRICTEST of the gap rule (bufferMin, set by admin) and the shortest allowed
  // booking (minBookingMin). This way the gap picker always binds: if admin sets
  // gap=90, all gaps < 90 are blocked — even if a short booking could technically
  // fill a 60-min gap. minBookingMin guarantees a sensible lower bound when the
  // gap rule is low or off.
  const deadGapLimit = Math.max(bufferMin || 0, minBookingMin != null ? minBookingMin : 0);
  if (
    !candidate ||
    !candidate.court ||
    !candidate.date ||
    !candidate.startTime ||
    !candidate.endTime
  ) {
    return { ok: false, reason: 'Mangler påkrevde felt (bane, dato, start, slutt)' };
  }
  const startMin = toMinHHMM(candidate.startTime);
  let endMin = toMinHHMM(candidate.endTime);
  // Over-midnight: if end is on/before start, the end is interpreted as the next
  // day. (The center may close after midnight, e.g. 23:30–00:30.) endMin ===
  // startMin is still invalid (zero duration).
  if (endMin < startMin) endMin += 1440;
  if (endMin === startMin) {
    return { ok: false, reason: 'Sluttid må være etter starttid' };
  }
  // Half-hour rule: start and end must land on the full/half hour (XX:00 or
  // XX:30). This is a business rule — quarter-past/quarter-to bookings make a
  // messy calendar and are not wanted. The buffer rule (30 min between bookings)
  // also makes the most sense when all bookings are rastered on half hours.
  if (startMin % 30 !== 0) {
    return { ok: false, reason: 'Starttid må være på hel eller halv time (XX:00 eller XX:30)' };
  }
  if (endMin % 30 !== 0) {
    return { ok: false, reason: 'Sluttid må være på hel eller halv time (XX:00 eller XX:30)' };
  }
  // WITHIN THE CENTRE'S OPENING HOURS, when the caller supplies them.
  //
  // The window is read exactly as `findFreeSlots` reads it, because the slot
  // list and this validator must agree about which bookings exist: a closing
  // time on or before opening means the centre closes the NEXT day (05:00–02:00),
  // and round-the-clock (00:00–00:00) becomes the whole 1440.
  //
  // A candidate on an over-midnight window needs the same shift the matrix
  // applies: 00:30 against a 05:00–02:00 window is the LATE part of that day,
  // not eleven hours before opening.
  if (openTime != null && closeTime != null) {
    const openMin = toMinHHMM(openTime);
    let closeMin = toMinHHMM(closeTime);
    if (closeMin <= openMin) closeMin += 1440;
    let s = startMin;
    let e = endMin;
    if (closeMin > 1440 && s < openMin) {
      s += 1440;
      e += 1440;
    }
    if (s < openMin || e > closeMin) {
      return {
        ok: false,
        reason: `Utenfor senterets åpningstid (${openTime}–${closeTime})`,
        // A CODE beside the prose, because this is the one refusal a caller
        // acts on rather than only displays: the portal offers an admin the
        // override, and matching on a Norwegian sentence to decide that would
        // break the day somebody rewords it.
        code: 'outside_opening_hours',
      };
    }
  }
  // Check conflicts with existing bookings on the same court and date. We ignore
  // the candidate's own id on update (id matches an existing one). First: narrow
  // to relevant bookings (same court + date, not itself).
  const sameCourtDay = existingBookings.filter(
    (b) => b.id !== candidate.id && b.court === candidate.court && b.date === candidate.date,
  );
  // Check direct overlap first (all bookings must be considered). This ALWAYS
  // applies, regardless of smartAllocation.
  for (const b of sameCourtDay) {
    const bStart = toMinHHMM(b.startTime);
    const bEnd = toMinHHMM(b.endTime);
    if (startMin < bEnd && endMin > bStart) {
      return {
        ok: false,
        reason: `Bane ${candidate.court} er allerede booket ${b.startTime}–${b.endTime}`,
        conflictId: b.id,
      };
    }
  }
  // GAP RULE — always applies (regardless of smart booking). Gaps between
  // bookings on the same court must be 0 (back-to-back) or larger than bufferMin.
  // Buffer check: ONLY against the nearest previous and nearest next booking.
  // Bookings further away are irrelevant — there may be other bookings in
  // between that fill the space.
  // Nearest previous = the one with the highest endTime that is <= startMin
  // Nearest next     = the one with the lowest startTime that is >= endMin
  let prevBooking: any = null;
  let nextBooking: any = null;
  for (const b of sameCourtDay) {
    const bStart = toMinHHMM(b.startTime);
    const bEnd = toMinHHMM(b.endTime);
    if (bEnd <= startMin) {
      // b is before the candidate
      if (!prevBooking || bEnd > toMinHHMM(prevBooking.endTime)) prevBooking = b;
    }
    if (bStart >= endMin) {
      // b is after the candidate
      if (!nextBooking || bStart < toMinHHMM(nextBooking.startTime)) nextBooking = b;
    }
  }
  // Gap between candidate and existing — must be 0 (contiguous) or at least the
  // shortest allowed booking (deadGapLimit). A gap smaller than this is "dead" —
  // too short for any other booking to fill. A gap equal to or larger than the
  // shortest booking is "usable" and is allowed (smart booking then decides
  // whether another court gives tighter placement).
  if (prevBooking) {
    const gap = startMin - toMinHHMM(prevBooking.endTime);
    if (gap > 0 && gap < deadGapLimit) {
      return {
        ok: false,
        reason: `Skaper ${gap} min hull etter booking ${prevBooking.startTime}–${prevBooking.endTime}. Hull må være 0 (sammenhengende) eller minst ${deadGapLimit} min — ellers blir det for kort til at noen andre får booket.`,
        conflictId: prevBooking.id,
      };
    }
    if (
      smartAllocation &&
      allowedDurations &&
      gap > 0 &&
      !gapFillableByDurations(gap, allowedDurations)
    ) {
      return {
        ok: false,
        reason: `Skaper et udelelig hull på ${gap} min etter booking ${prevBooking.startTime}–${prevBooking.endTime} (kan ikke fylles av tillatte varigheter). Velg en varighet som ikke etterlater et slikt hull, eller skru av smart allokering.`,
        conflictId: prevBooking.id,
      };
    }
  }
  if (nextBooking) {
    const gap = toMinHHMM(nextBooking.startTime) - endMin;
    if (gap > 0 && gap < deadGapLimit) {
      return {
        ok: false,
        reason: `Skaper ${gap} min hull før booking ${nextBooking.startTime}. Hull må være 0 (sammenhengende) eller minst ${deadGapLimit} min — ellers blir det for kort til at noen andre får booket.`,
        conflictId: nextBooking.id,
      };
    }
    if (
      smartAllocation &&
      allowedDurations &&
      gap > 0 &&
      !gapFillableByDurations(gap, allowedDurations)
    ) {
      return {
        ok: false,
        reason: `Skaper et udelelig hull på ${gap} min før booking ${nextBooking.startTime} (kan ikke fylles av tillatte varigheter). Velg en varighet som ikke etterlater et slikt hull, eller skru av smart allokering.`,
        conflictId: nextBooking.id,
      };
    }
  }
  // SMART BOOKING — optional rule ON TOP of the gap rule. Limits which courts the
  // user may pick: if the candidate leaves "slack" on its court (booked into a
  // large free block with spare time), but there is another court where the same
  // booking fits MORE TIGHTLY (less surplus time), the worse court is LOCKED —
  // the user is steered toward the better one. Example: a 90-min booking should
  // not be placeable in a 2-hour opening on court 1 when court 2 has an opening
  // of exactly 90 min. Requires totalCourts + all bookings. When smart booking is
  // OFF, the user may pick any gap-legal court.
  if (smartAllocation && totalCourts && totalCourts > 1) {
    // Cost of placing the candidate on a court: how much "trapped" gap (between an
    // EXISTING booking and the candidate) the placement creates. An empty court
    // creates no trapped gaps (cost 0) — a booking next to an existing one with a
    // gap creates a gap (cost = the minutes). Back-to-back (gap 0) gives a small
    // bonus (consolidation). Lower = better. null = the court is occupied or the
    // placement would create a dead gap (not a valid alternative).
    //
    // This fixes the old "smallest opening" logic that wrongly forced a court with
    // an earlier booking (smaller opening) even when it created a gap — instead of
    // an empty court that does not fragment.
    const fragScore = (court: any) => {
      const bsOnCourt = existingBookings.filter(
        (b) => b.id !== candidate.id && b.court === court && b.date === candidate.date,
      );
      for (const b of bsOnCourt) {
        const bS = toMinHHMM(b.startTime),
          bE = toMinHHMM(b.endTime);
        if (startMin < bE && endMin > bS) return null; // occupied in the time range
      }
      let nearestPrevEnd: number | null = null,
        nearestNextStart: number | null = null;
      for (const b of bsOnCourt) {
        const bS = toMinHHMM(b.startTime),
          bE = toMinHHMM(b.endTime);
        if (bE <= startMin && (nearestPrevEnd == null || bE > nearestPrevEnd)) nearestPrevEnd = bE;
        if (bS >= endMin && (nearestNextStart == null || bS < nearestNextStart))
          nearestNextStart = bS;
      }
      let score = 0,
        touches = 0;
      // AN ALTERNATIVE MUST BE ONE THE USER COULD ACTUALLY TAKE — PRODUCT
      // CHANGE, decided by the owner 2026-10-01.
      //
      // `fragScore` ranked courts by trapped minutes and never applied the
      // gap-fillability rule the branches above enforce. So a court
      // `validateBooking` REJECTS could still win the comparison, and every
      // other court was locked with «Bane 1 gir tettere plassering. Velg bane
      // 1» — when bane 1 could not be chosen either.
      //
      // Nothing could. On a day with four bookings on four courts, 13:00
      // offered no court at all, on any duration, and the time simply
      // disappeared from «Ledige tider». Returning null here is what the
      // function already does for a dead gap, and means the same thing: not a
      // real alternative, so it steers nobody.
      const unfillable = (g: number) =>
        g > 0 && allowedDurations && !gapFillableByDurations(g, allowedDurations);
      if (nearestPrevEnd != null) {
        const g = startMin - nearestPrevEnd;
        if (g > 0 && g < deadGapLimit) return null; // would create a dead gap
        if (unfillable(g)) return null;
        if (g === 0) touches++;
        else score += g; // trapped gap is penalized
      }
      if (nearestNextStart != null) {
        const g = nearestNextStart - endMin;
        if (g > 0 && g < deadGapLimit) return null;
        if (unfillable(g)) return null;
        if (g === 0) touches++;
        else score += g;
      }
      return score - touches; // back-to-back gives a small consolidation bonus
    };
    const myCourts =
      enabledCourts && enabledCourts.length > 0
        ? enabledCourts
        : Array.from({ length: totalCourts }, (_, i) => i + 1);
    const myScore = fragScore(candidate.court);
    if (myScore != null) {
      for (const c of myCourts) {
        if (c === candidate.court) continue;
        if (excludeCourts && excludeCourts.includes(c)) continue; // also booked in the same block -> not a real alternative
        const s = fragScore(c);
        // Strictly lower cost = another court fragments less -> steer there.
        if (s != null && s < myScore) {
          return {
            ok: false,
            reason: `Bane ${c} gir tettere plassering (mindre ubrukt tid / færre hull). Velg bane ${c}, eller skru av smart allokering i innstillinger for å booke fritt.`,
            betterCourt: c,
          };
        }
      }
    }
  }
  return { ok: true };
}

// Create a booking (validates first). Returns { ok, booking?, reason? }.
// source indicates origin: 'user' (regular user), 'admin' (padel center/admin),
// 'tournament' (created via a tournament — integration coming). Recurring is
// derived from seriesId and overrides the color coding in the view.
export function createBooking(
  {
    court,
    date,
    startTime,
    durationMin,
    endTime,
    bookedBy = null,
    notes = '',
    paymentStatus = 'free',
    priceNok = 0,
    title = null,
    seriesId = null,
    recur = null,
    source = 'user',
  }: any,
  existingBookings: any[] = [],
  opts: any = {},
): any {
  // Compute endTime if only durationMin is given
  let effectiveEnd = endTime;
  if (!effectiveEnd && durationMin) {
    effectiveEnd = fromMinHHMM(toMinHHMM(startTime) + durationMin);
  }
  const candidate: any = {
    id: `book_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    court,
    date,
    startTime,
    endTime: effectiveEnd,
    durationMin: durationMin || toMinHHMM(effectiveEnd) - toMinHHMM(startTime),
    bookedBy: bookedBy || { name: '', phone: '', email: '' },
    notes,
    paymentStatus,
    priceNok,
    source,
    createdAt: Date.now(),
  };
  // Preserve optional title (admin), series id and recurrence pattern.
  if (title) candidate.title = title;
  if (seriesId) candidate.seriesId = seriesId;
  // eslint-disable-next-line prefer-rest-params
  const firstArg = arguments[0];
  if (Array.isArray(firstArg?.classIds) && firstArg.classIds.length)
    candidate.classIds = [...firstArg.classIds];
  if (recur) candidate.recur = recur;
  const v = validateBooking(candidate, existingBookings, opts);
  if (!v.ok) return { ok: false, reason: v.reason, conflictId: v.conflictId };
  return { ok: true, booking: candidate };
}

// Get bookings for a given date (sorted by start time + court)
export function getBookingsForDay(bookings: any[], date: string): any[] {
  return (bookings || [])
    .filter((b) => b.date === date)
    .sort((a, b) => {
      const cmp = toMinHHMM(a.startTime) - toMinHHMM(b.startTime);
      if (cmp !== 0) return cmp;
      return (a.court || 0) - (b.court || 0);
    });
}

// Get the effective facilities for a court: per-court override over the globals.
export function courtFacilities(courtNum, courtMeta, courtDefaults) {
  const def = courtDefaults || { courtType: 'double', runoff: false, streaming: false };
  const m = (courtMeta && courtMeta[courtNum]) || {};
  return {
    courtType: m.courtType != null ? m.courtType : def.courtType || 'double',
    runoff: m.runoff != null ? m.runoff : !!def.runoff,
    streaming: m.streaming != null ? m.streaming : !!def.streaming,
  };
}

// Get the display name for a court: nickname if set, otherwise "Bane N".
export function courtDisplayName(courtNum, courtMeta) {
  const m = courtMeta && courtMeta[courtNum];
  return m && m.nickname && m.nickname.trim() ? m.nickname.trim() : `Bane ${courtNum}`;
}

// ─── PHYSICAL COURT STATE (#271) ───
// `enabledCourts` says which courts are OFFERED; `outOfService` says whether a
// court is USABLE. They are independent, and a court can be offered AND broken
// — which is the case this exists for.
//
// ONE reading of the shape, for every caller. The API refuses a booking on a
// broken court and the staff screens have to mark it as broken; if each parsed
// `courtMeta[c].outOfService` for itself they would disagree the first time the
// shape grew a field, and the screen would call a court usable that the server
// refuses. `courtOutOfService` in apps/api delegates here for that reason.
//
// The `reason` is an OPERATIONAL note written by staff, for staff — never a
// customer-facing message.
export function courtOutOfServiceState(courtNum, courtMeta) {
  const entry = courtMeta && courtMeta[courtNum];
  const state = entry && entry.outOfService;
  if (!state) return null;
  return {
    court: Number(courtNum),
    reason: state.reason != null ? String(state.reason) : null,
    since: typeof state.since === 'number' && Number.isFinite(state.since) ? state.since : null,
  };
}

// Is this court physically out of service? The predicate, for callers that do
// not need the reason.
export function isCourtOutOfService(courtNum, courtMeta) {
  return courtOutOfServiceState(courtNum, courtMeta) != null;
}

// THE SERVER OWNS THIS SUBTREE (#271).
//
// `booking_config_v1` has two writers: the settings editor replaces the whole
// document, and the out-of-service command edits `courtMeta[*].outOfService`.
// A document the editor read before a court was closed would put the court back
// in service on save — so the blob write does not get to say anything about it.
// These two functions are that boundary, and they are shared so the route that
// ENFORCES the ownership and the command that EXERCISES it cannot disagree
// about which fields are owned.

/** Every court's out-of-service state, lifted out of a config document. */
export function readCourtOutOfServiceStates(courtMeta) {
  const out = {};
  for (const key of Object.keys(courtMeta || {})) {
    const state = courtMeta[key] && courtMeta[key].outOfService;
    if (state) out[key] = state;
  }
  return out;
}

/**
 * `courtMeta` with the out-of-service states replaced by `owned`.
 *
 * Whatever the caller had in the field is DROPPED, not merged: the point is
 * that a whole-document write cannot set, change or clear it, and merging would
 * let it do all three.
 */
export function withCourtOutOfServiceStates(courtMeta, owned) {
  const next = {};
  const keys = new Set([...Object.keys(courtMeta || {}), ...Object.keys(owned || {})]);
  for (const key of keys) {
    const entry = { ...((courtMeta && courtMeta[key]) || {}) };
    const state = owned && owned[key];
    if (state) entry.outOfService = state;
    else delete entry.outOfService;
    next[key] = entry;
  }
  return next;
}

// ─── PRICE ZONES ───
// A price zone matches bookings based on weekday, date (holiday) and time range.
// Format:
//   {
//     id: 'zone_1',
//     name: 'Primetime',
//     pricePerHour: 400,           // kr per hour (rounded to nearest whole krone)
//     daysOfWeek: ['mon','tue','wed','thu','fri'],  // empty = all days
//     includeHolidays: false,      // if true also applies on holidays
//     onlyHolidays: false,         // if true applies ONLY on holidays (overrides daysOfWeek)
//     timeRanges: [{start:'17:00', end:'22:00'}],   // empty = whole day
//     priority: 10,                // higher = checked first
//   }
//
// A booking matches a zone if: weekday matches (or holiday and includeHolidays)
// AND the start time falls within at least one timeRange.
//
// Price calculation: if a booking spans multiple zones (e.g. 17:30-19:30 where
// 17-18 is Standard and 18-22 is Primetime), we price per minute in each zone.
//
// THERE IS NO DEFAULT ZONE (#1312, owner decision 2026-11-03). A club starts
// with no zones, and a club with no zones has no court price — see
// `courtPricesSet` below. The 250 kr «Standard» zone the browser used to invent
// when nothing was saved (`BOOKING_DEFAULT_ZONES`, inherited from the original
// artifact) is deleted rather than kept as an example: a constant named «default»
// with a price in it is exactly what a future fallback would reach for. The zone
// editor's «add zone» template is its own (`BookingZoneEditor`).

export const DAY_OF_WEEK_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const DAY_OF_WEEK_LABELS: Record<string, string> = {
  mon: 'Man',
  tue: 'Tir',
  wed: 'Ons',
  thu: 'Tor',
  fri: 'Fre',
  sat: 'Lør',
  sun: 'Søn',
};

// Holidays 2024–2027 for Norway, Sweden and Denmark. Each entry: 'YYYY-MM-DD'.
// Fixed + movable (Easter etc.) are entered manually. Admin can additionally
// add/remove days per club via config (holidayCountry + holidayExtra/holidayRemoved).
export const HOLIDAYS: Record<string, Set<string>> = {
  no: new Set([
    '2024-01-01',
    '2024-05-01',
    '2024-05-17',
    '2024-12-25',
    '2024-12-26',
    '2025-01-01',
    '2025-05-01',
    '2025-05-17',
    '2025-12-25',
    '2025-12-26',
    '2026-01-01',
    '2026-05-01',
    '2026-05-17',
    '2026-12-25',
    '2026-12-26',
    '2027-01-01',
    '2027-05-01',
    '2027-05-17',
    '2027-12-25',
    '2027-12-26',
    // Movable: Maundy Thursday, Good Friday, Easter Eve, Easter Sunday + Monday, Ascension Day, Whit Sunday + Monday
    '2024-03-28',
    '2024-03-29',
    '2024-03-31',
    '2024-04-01',
    '2024-05-09',
    '2024-05-19',
    '2024-05-20',
    '2025-04-17',
    '2025-04-18',
    '2025-04-20',
    '2025-04-21',
    '2025-05-29',
    '2025-06-08',
    '2025-06-09',
    '2026-04-02',
    '2026-04-03',
    '2026-04-05',
    '2026-04-06',
    '2026-05-14',
    '2026-05-24',
    '2026-05-25',
    '2027-03-25',
    '2027-03-26',
    '2027-03-28',
    '2027-03-29',
    '2027-05-06',
    '2027-05-16',
    '2027-05-17',
  ]),
  se: new Set([
    // Fixed: New Year, Epiphany (6.1), 1 May, national day (6.6), Christmas
    '2024-01-01',
    '2024-01-06',
    '2024-05-01',
    '2024-06-06',
    '2024-12-24',
    '2024-12-25',
    '2024-12-26',
    '2024-12-31',
    '2025-01-01',
    '2025-01-06',
    '2025-05-01',
    '2025-06-06',
    '2025-12-24',
    '2025-12-25',
    '2025-12-26',
    '2025-12-31',
    '2026-01-01',
    '2026-01-06',
    '2026-05-01',
    '2026-06-06',
    '2026-12-24',
    '2026-12-25',
    '2026-12-26',
    '2026-12-31',
    '2027-01-01',
    '2027-01-06',
    '2027-05-01',
    '2027-06-06',
    '2027-12-24',
    '2027-12-25',
    '2027-12-26',
    '2027-12-31',
    // Movable: Good Friday, Easter Sunday, Easter Monday, Ascension Day, Whitsun, Midsummer Eve/Day
    '2024-03-29',
    '2024-03-31',
    '2024-04-01',
    '2024-05-09',
    '2024-05-19',
    '2024-06-21',
    '2024-06-22',
    '2025-04-18',
    '2025-04-20',
    '2025-04-21',
    '2025-05-29',
    '2025-06-08',
    '2025-06-20',
    '2025-06-21',
    '2026-04-03',
    '2026-04-05',
    '2026-04-06',
    '2026-05-14',
    '2026-05-24',
    '2026-06-19',
    '2026-06-20',
    '2027-03-26',
    '2027-03-28',
    '2027-03-29',
    '2027-05-06',
    '2027-05-16',
    '2027-06-25',
    '2027-06-26',
  ]),
  dk: new Set([
    '2024-01-01',
    '2024-12-24',
    '2024-12-25',
    '2024-12-26',
    '2024-12-31',
    '2025-01-01',
    '2025-12-24',
    '2025-12-25',
    '2025-12-26',
    '2025-12-31',
    '2026-01-01',
    '2026-12-24',
    '2026-12-25',
    '2026-12-26',
    '2026-12-31',
    '2027-01-01',
    '2027-12-24',
    '2027-12-25',
    '2027-12-26',
    '2027-12-31',
    // Movable: Maundy Thursday, Good Friday, Easter Sunday, Easter Monday, General Prayer Day (abolished 2024+), Ascension Day, Whitsun, Whit Monday
    '2024-03-28',
    '2024-03-29',
    '2024-03-31',
    '2024-04-01',
    '2024-05-09',
    '2024-05-19',
    '2024-05-20',
    '2025-04-17',
    '2025-04-18',
    '2025-04-20',
    '2025-04-21',
    '2025-05-29',
    '2025-06-08',
    '2025-06-09',
    '2026-04-02',
    '2026-04-03',
    '2026-04-05',
    '2026-04-06',
    '2026-05-14',
    '2026-05-24',
    '2026-05-25',
    '2027-03-25',
    '2027-03-26',
    '2027-03-28',
    '2027-03-29',
    '2027-05-06',
    '2027-05-16',
    '2027-05-17',
  ]),
};

// Backwards-compatible alias.
export const NORWEGIAN_HOLIDAYS_FIXED = HOLIDAYS.no;

// Names of fixed holidays (same date every year), key = 'MM-DD'.
export const HOLIDAY_NAMES_FIXED: Record<string, Record<string, string>> = {
  no: {
    '01-01': 'Nyttårsdag',
    '05-01': 'Arbeidernes dag',
    '05-17': 'Grunnlovsdagen',
    '12-25': '1. juledag',
    '12-26': '2. juledag',
  },
  se: {
    '01-01': 'Nyårsdagen',
    '01-06': 'Trettondedag jul',
    '05-01': 'Första maj',
    '06-06': 'Sveriges nationaldag',
    '12-24': 'Julafton',
    '12-25': 'Juldagen',
    '12-26': 'Annandag jul',
    '12-31': 'Nyårsafton',
  },
  dk: {
    '01-01': 'Nytårsdag',
    '12-24': 'Juleaften',
    '12-25': '1. juledag',
    '12-26': '2. juledag',
    '12-31': 'Nytårsaften',
  },
};

// Names of movable holidays (Easter etc.), key = full ISO date.
export const HOLIDAY_NAMES_MOVABLE: Record<string, Record<string, string>> = {
  no: {
    '2024-03-28': 'Skjærtorsdag',
    '2024-03-29': 'Langfredag',
    '2024-03-31': '1. påskedag',
    '2024-04-01': '2. påskedag',
    '2024-05-09': 'Kristi himmelfartsdag',
    '2024-05-19': '1. pinsedag',
    '2024-05-20': '2. pinsedag',
    '2025-04-17': 'Skjærtorsdag',
    '2025-04-18': 'Langfredag',
    '2025-04-20': '1. påskedag',
    '2025-04-21': '2. påskedag',
    '2025-05-29': 'Kristi himmelfartsdag',
    '2025-06-08': '1. pinsedag',
    '2025-06-09': '2. pinsedag',
    '2026-04-02': 'Skjærtorsdag',
    '2026-04-03': 'Langfredag',
    '2026-04-05': '1. påskedag',
    '2026-04-06': '2. påskedag',
    '2026-05-14': 'Kristi himmelfartsdag',
    '2026-05-24': '1. pinsedag',
    '2026-05-25': '2. pinsedag',
    '2027-03-25': 'Skjærtorsdag',
    '2027-03-26': 'Langfredag',
    '2027-03-28': '1. påskedag',
    '2027-03-29': '2. påskedag',
    '2027-05-06': 'Kristi himmelfartsdag',
    '2027-05-16': '1. pinsedag',
    '2027-05-17': '2. pinsedag',
  },
  se: {
    '2024-03-29': 'Långfredagen',
    '2024-03-31': 'Påskdagen',
    '2024-04-01': 'Annandag påsk',
    '2024-05-09': 'Kristi himmelfärds dag',
    '2024-05-19': 'Pingstdagen',
    '2024-06-21': 'Midsommarafton',
    '2024-06-22': 'Midsommardagen',
    '2025-04-18': 'Långfredagen',
    '2025-04-20': 'Påskdagen',
    '2025-04-21': 'Annandag påsk',
    '2025-05-29': 'Kristi himmelfärds dag',
    '2025-06-08': 'Pingstdagen',
    '2025-06-20': 'Midsommarafton',
    '2025-06-21': 'Midsommardagen',
    '2026-04-03': 'Långfredagen',
    '2026-04-05': 'Påskdagen',
    '2026-04-06': 'Annandag påsk',
    '2026-05-14': 'Kristi himmelfärds dag',
    '2026-05-24': 'Pingstdagen',
    '2026-06-19': 'Midsommarafton',
    '2026-06-20': 'Midsommardagen',
    '2027-03-26': 'Långfredagen',
    '2027-03-28': 'Påskdagen',
    '2027-03-29': 'Annandag påsk',
    '2027-05-06': 'Kristi himmelfärds dag',
    '2027-05-16': 'Pingstdagen',
    '2027-06-25': 'Midsommarafton',
    '2027-06-26': 'Midsommardagen',
  },
  dk: {
    '2024-03-28': 'Skærtorsdag',
    '2024-03-29': 'Langfredag',
    '2024-03-31': 'Påskedag',
    '2024-04-01': '2. påskedag',
    '2024-05-09': 'Kristi himmelfartsdag',
    '2024-05-19': 'Pinsedag',
    '2024-05-20': '2. pinsedag',
    '2025-04-17': 'Skærtorsdag',
    '2025-04-18': 'Langfredag',
    '2025-04-20': 'Påskedag',
    '2025-04-21': '2. påskedag',
    '2025-05-29': 'Kristi himmelfartsdag',
    '2025-06-08': 'Pinsedag',
    '2025-06-09': '2. pinsedag',
    '2026-04-02': 'Skærtorsdag',
    '2026-04-03': 'Langfredag',
    '2026-04-05': 'Påskedag',
    '2026-04-06': '2. påskedag',
    '2026-05-14': 'Kristi himmelfartsdag',
    '2026-05-24': 'Pinsedag',
    '2026-05-25': '2. pinsedag',
    '2027-03-25': 'Skærtorsdag',
    '2027-03-26': 'Langfredag',
    '2027-03-28': 'Påskedag',
    '2027-03-29': '2. påskedag',
    '2027-05-06': 'Kristi himmelfartsdag',
    '2027-05-16': 'Pinsedag',
    '2027-05-17': '2. pinsedag',
  },
};

// Look up the name of a holiday for a given country. Returns name or null.
export function holidayName(isoDate: string, country = 'no'): string | null {
  const c = HOLIDAY_NAMES_FIXED[country] ? country : 'no';
  const mmdd = isoDate.slice(5);
  if (HOLIDAY_NAMES_FIXED[c][mmdd]) return HOLIDAY_NAMES_FIXED[c][mmdd];
  if (HOLIDAY_NAMES_MOVABLE[c] && HOLIDAY_NAMES_MOVABLE[c][isoDate])
    return HOLIDAY_NAMES_MOVABLE[c][isoDate];
  return null;
}

// Get the effective holiday set based on config: chosen country + custom
// additions (holidayExtra) minus removals (holidayRemoved). Returns a Set of ISO.
export function getHolidaySet(cfg: any): Set<string> {
  const country = (cfg && cfg.holidayCountry) || 'no';
  const base = HOLIDAYS[country] || HOLIDAYS.no;
  const set = new Set(base);
  ((cfg && cfg.holidayExtra) || []).forEach((d: string) => set.add(d));
  ((cfg && cfg.holidayRemoved) || []).forEach((d: string) => set.delete(d));
  return set;
}

// Config-aware holiday check. Without cfg it falls back to the Norwegian base set.
export function isHoliday(isoDate: string, cfg: any = null): boolean {
  if (cfg) return getHolidaySet(cfg).has(isoDate);
  return HOLIDAYS.no.has(isoDate);
}

export function isNorwegianHoliday(isoDate: string): boolean {
  return HOLIDAYS.no.has(isoDate);
}

export function dayOfWeekKey(isoDate: string): string {
  // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const d = new Date(isoDate + 'T00:00:00');
  return DAY_OF_WEEK_KEYS[d.getDay()];
}

// Check whether a zone matches date + time (in minutes since midnight).
export function zoneMatches(zone: any, isoDate: string, atMin: number, cfg: any = null): boolean {
  const holiday = isHoliday(isoDate, cfg);
  // onlyHolidays: must be a holiday — weekday ignored
  if (zone.onlyHolidays) {
    if (!holiday) return false;
  } else {
    // excludeHolidays: the zone NEVER applies on holidays (e.g. weekday price
    // that should not apply on 17 May), regardless of weekday.
    if (zone.excludeHolidays && holiday) return false;
    // Regular weekday check
    const dow = dayOfWeekKey(isoDate);
    const dayList = zone.daysOfWeek || [];
    const dayMatches = dayList.length === 0 || dayList.includes(dow);
    if (!dayMatches) {
      // If weekday does not match, it can still match if it is a holiday and includeHolidays
      if (!(holiday && zone.includeHolidays)) return false;
    }
  }
  // Time-range check
  const ranges = zone.timeRanges || [];
  if (ranges.length === 0) return true;
  for (const r of ranges) {
    const rs = toMinHHMM(r.start);
    const re = toMinHHMM(r.end);
    if (atMin >= rs && atMin < re) return true;
  }
  return false;
}

// Find the zone that covers a specific time (returns the highest-priority match).
export function findZoneAt(zones: any[], isoDate: string, atMin: number, cfg: any = null): any {
  const sorted = [...zones].sort((a, b) => (b.priority || 0) - (a.priority || 0));
  for (const z of sorted) {
    if (zoneMatches(z, isoDate, atMin, cfg)) return z;
  }
  return null;
}

// Compute the total price for a booking by summing the per-minute price in each
// matching zone. A booking spanning multiple zones: each minute is priced per
// its zone. Each zone has a priceMode:
//   'replace' (default) — the zone price replaces the default price for those minutes
//   'add'               — the zone price is ADDED to the default price
export function calcBookingPrice(
  zones: any[],
  isoDate: string,
  startTime: string,
  endTime: string,
  cfg: any = null,
): any {
  const startMin = toMinHHMM(startTime);
  const endMin = toMinHHMM(endTime);
  if (endMin <= startMin) return { total: 0, breakdown: [] };
  // Default/base price per hour — applies to minutes where no zone matches, and
  // is added when a matching zone has priceMode 'add'.
  const defaultPrice = (cfg && cfg.defaultPricePerHour) || 0;

  // Return an "effective zone" for a given minute: name, price/hour and id.
  // Takes priceMode into account (add layers the default price on top).
  const effectiveAt = (atMin: number) => {
    const z = findZoneAt(zones, isoDate, atMin, cfg);
    if (!z) {
      // No zone → default price (can be 0 = free).
      return { id: '__default__', name: 'Standard', pricePerHour: defaultPrice };
    }
    const mode = z.priceMode || 'replace';
    if (mode === 'add') {
      return {
        id: z.id + '+base',
        name: defaultPrice > 0 ? `${z.name} + standard` : z.name,
        pricePerHour: (z.pricePerHour || 0) + defaultPrice,
      };
    }
    return { id: z.id, name: z.name, pricePerHour: z.pricePerHour || 0 };
  };

  const breakdown: any[] = []; // [{ zoneName, fromMin, toMin, durationMin, kr }]
  let cursor = startMin;
  let currentZone = effectiveAt(cursor);
  let segmentStart = cursor;

  // Walk each minute — when the "effective zone" changes, close the segment and open a new one
  while (cursor < endMin) {
    const zoneAtCursor = effectiveAt(cursor);
    if (zoneAtCursor.id !== currentZone.id) {
      if (cursor > segmentStart) {
        const dur = cursor - segmentStart;
        const kr = Math.round((currentZone.pricePerHour || 0) * (dur / 60));
        breakdown.push({
          zoneName: currentZone.name,
          zoneId: currentZone.id,
          fromMin: segmentStart,
          toMin: cursor,
          durationMin: dur,
          kr,
        });
      }
      currentZone = zoneAtCursor;
      segmentStart = cursor;
    }
    cursor++;
  }
  // Close the last segment
  if (endMin > segmentStart) {
    const dur = endMin - segmentStart;
    const kr = Math.round((currentZone.pricePerHour || 0) * (dur / 60));
    breakdown.push({
      zoneName: currentZone.name,
      zoneId: currentZone.id,
      fromMin: segmentStart,
      toMin: endMin,
      durationMin: dur,
      kr,
    });
  }
  const total = breakdown.reduce((s, x) => s + x.kr, 0);
  return { total, breakdown };
}

// ─── NO PRICE UNTIL ZONES EXIST (#1312, owner decision 2026-11-03) ───
//
// «A club without saved price zones cannot take paid bookings. The app shows
// "Pris ikke satt", and nothing is charged until the club sets its prices.
// Shown and charged must never differ.»
//
// ONE RULE, run by both sides. The browser used to invent a 250 kr «Standard»
// zone when `booking_zones_v1` was absent or empty and showed that price, while
// the server priced the same slot from `defaultPricePerHour` — so a club with no
// zones and a 300 kr default was shown 250 and would have been charged 300. Both
// now ask this, and a club with no saved zone has no court price at all: not the
// default price, not free, not a guess.
//
// COURTS ONLY. `calcBookingPrice` itself is unchanged: an asset (equipment) is
// priced by the same function from its OWN zones and its own default price, and
// a machine with no zones and a default of 60 kr/h costs 60 kr/h
// (docs/domains/ASSET_BOOKING.md). Inside a club that HAS zones, minutes no zone
// covers are still priced at `defaultPricePerHour` (0 = free) as before.

/**
 * Has the club SET its court prices? True when it has at least one saved price
 * zone (`booking_zones_v1` is a non-empty array). Absent, `[]`, or anything that
 * is not an array means «not set».
 */
export function courtPricesSet(zones: unknown): boolean {
  return Array.isArray(zones) && zones.length > 0;
}

/**
 * What a COURT booking costs, or `null` when the club has not set its prices
 * (`courtPricesSet`). With zones it is exactly `calcBookingPrice`.
 *
 * `null` is not zero: a caller must show «Pris ikke satt» and must not charge,
 * and must never turn it into «free» or into the default price.
 */
export function calcCourtBookingPrice(
  zones: unknown,
  isoDate: string,
  startTime: string,
  endTime: string,
  cfg: any = null,
  tier: CourtPriceTier = 'member',
): { total: number; breakdown: any[] } | null {
  if (!courtPricesSet(zones)) return null;
  // THE MEMBER PATH IS TODAY'S, UNTOUCHED. Every existing caller passes no tier
  // and lands here exactly as before (#833 slice 1).
  if (tier === 'member') return calcBookingPrice(zones as any[], isoDate, startTime, endTime, cfg);
  if (tier === 'guest')
    return calcGuestBookingPrice(zones as any[], isoDate, startTime, endTime, cfg);
  // A tier nobody defined is not «member»: a stored value this code does not
  // know must not be resolved into the cheaper price. Not priced at all.
  return null;
}

// ─── THE GUEST PRICE (#833, owner decision 2026-11-04) ───
//
// «Non-members may book and pay, at a guest price each venue sets.» The answer
// to «what does a guest pay» is, per the decision:
//
//   1. PER ZONE. Each zone may carry a `guestPricePerHour`; the venue may carry a
//      `guestDefaultPricePerHour` for the minutes no zone covers (stored in its
//      own document, `booking_guest_prices_v1`, because it is a PRICE and prices
//      are `booking.rules.manage` — see `withGuestPrices`).
//   2. NEVER THE MEMBER PRICE. A minute whose zone has no guest price — or that
//      no zone covers while the venue has no guest default — has no guest price,
//      and then the whole booking has none: `null`, «guest price not set».
//      Not the member price, not free, not a guess (the #1312 rule, applied to
//      the second tier).
//   3. NO ZONES, NO PRICE (#1312) — answered by `calcCourtBookingPrice` above,
//      before this is ever asked.
//
// «Not set» is NOT zero. 0 is a real guest price (free guest booking, allowed by
// decision 7 with a warning in settings); absent, empty, negative or not a number
// is «not set». The member path keeps its own reading, where an absent default
// is 0 kr — that is today's behaviour and it does not move.
//
// ONE MINUTE WALK, NOT TWO. The guest price is `calcBookingPrice` run over a GUEST
// VIEW of the same zones — each zone's `pricePerHour` replaced by its guest price,
// the default by the guest default — so zone selection (priority, weekdays,
// holidays, time ranges), `priceMode`, segmenting and rounding are the member
// price's own code. Only «is every minute priced for a guest?» is asked here.
//
// `priceMode: 'add'` layers a zone's price on the venue default for members; for
// a guest it layers the zone's guest price on the GUEST default, so an 'add' zone
// needs both to be set. (Reading of decision 1, stated in the PR: «unset is not
// zero» holds for the base as much as for the zone.)

/** Which price a court booking is charged at. Decided by the server, never by a request. */
export type CourtPriceTier = 'member' | 'guest';

/** Every tier, for a CHECK constraint or a guard to compare against. */
export const COURT_PRICE_TIERS: readonly CourtPriceTier[] = ['member', 'guest'];

/**
 * A guest price as stored, or `null` for «not set».
 *
 * A finite number ≥ 0 is a price (0 = free). Anything else — absent, `null`, an
 * empty field, a string, `NaN`, a negative number — is not set, and is never read
 * as 0.
 */
export function guestPriceOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * The booking configuration with the venue's guest default price in it.
 *
 * The guest default is stored in its own document (`booking_guest_prices_v1`,
 * written under `booking.rules.manage`), while the engine reads prices from the
 * config object. ONE FIELD is copied and nothing else, so a guest-price document
 * can never override an opening hour or a court.
 */
export function withGuestPrices(cfg: any, guestPrices: unknown): any {
  const value =
    guestPrices && typeof guestPrices === 'object' && !Array.isArray(guestPrices)
      ? (guestPrices as { guestDefaultPricePerHour?: unknown }).guestDefaultPricePerHour
      : undefined;
  return { ...(cfg ?? {}), guestDefaultPricePerHour: value };
}

/**
 * What a GUEST pays for a court, or `null` when the venue has not set a guest
 * price for every minute of it. Assumes zones exist (`calcCourtBookingPrice`
 * asks that first). Same shape as `calcBookingPrice`.
 */
function calcGuestBookingPrice(
  zones: any[],
  isoDate: string,
  startTime: string,
  endTime: string,
  cfg: any,
): { total: number; breakdown: any[] } | null {
  const guestDefault = guestPriceOf(cfg?.guestDefaultPricePerHour);
  const startMin = toMinHHMM(startTime);
  const endMin = toMinHHMM(endTime);
  // Every minute must have a guest price — the same zone the member walk picks.
  for (let at = startMin; at < endMin; at++) {
    const z = findZoneAt(zones, isoDate, at, cfg);
    if (!z) {
      if (guestDefault === null) return null;
      continue;
    }
    if (guestPriceOf(z.guestPricePerHour) === null) return null;
    if ((z.priceMode || 'replace') === 'add' && guestDefault === null) return null;
  }
  const guestZones = zones.map((z) => ({
    ...z,
    pricePerHour: guestPriceOf(z?.guestPricePerHour) ?? 0,
  }));
  return calcBookingPrice(guestZones, isoDate, startTime, endTime, {
    ...(cfg ?? {}),
    defaultPricePerHour: guestDefault ?? 0,
  });
}

// ─── WHETHER GUESTS MAY BOOK, AND HOW FAR AHEAD (#833, decision 2026-11-04) ───
//
// Two fields in `booking_config_v1`, beside `bookingWindowDays`, written by the
// settings editor under `court.manage` (decision 9):
//
//   guestBookingEnabled     guests may book here. ONLY `true` means yes: absent,
//                           `false`, «true» as a string — every venue that never
//                           opted in — means no. That is the default, and it is
//                           today's behaviour (decision: «the default stays off»).
//   guestBookingWindowDays  how far ahead a guest may book, counting today.
//                           Read like `bookingWindowDays` (missing, zero,
//                           negative or not a number is «not set»), then: not set
//                           is the MEMBER window, and it is NEVER LONGER than the
//                           member window (decision 3) — a guest who could book
//                           further ahead than a member reverses the reason to join.

/** What a venue's guest policy says, resolved. */
export interface GuestBookingPolicy {
  /** Guests may book at this venue. */
  enabled: boolean;
  /** How many days ahead a guest may book, counting today; ≤ the member window. */
  windowDays: number;
}

/** The venue's guest policy. Use this, never the raw fields. */
export function resolveGuestBookingPolicy(config?: any): GuestBookingPolicy {
  const memberWindow = resolveBookingWindowDays(config);
  const raw = Number(config?.guestBookingWindowDays);
  const whole = Number.isFinite(raw) ? Math.floor(raw) : NaN;
  const windowDays = whole >= 1 ? Math.min(whole, memberWindow) : memberWindow;
  return { enabled: config?.guestBookingEnabled === true, windowDays };
}

// Slot-aggregated availability: a list of time slots with the number of free
// courts. Returns [{ startTime, endTime, freeCourts, totalCourts, freeBaneList:
// [int] }] for a given day, given durationMin and totalCourts.
export function findFreeSlots(
  bookings: any[],
  isoDate: string,
  totalCourts: number,
  durationMin: number,
  {
    openTime = '07:00',
    closeTime = '23:00',
    bufferMin = BOOKING_DEFAULT_BUFFER_MIN,
    stepMin = 30,
    smartAllocation = true,
    enabledCourts = null,
    minBookingMin = null,
    allowedDurations = null,
  }: any = {},
): any[] {
  const openMin = toMinHHMM(openTime);
  let closeMin = toMinHHMM(closeTime);
  // Over-midnight: if closing is on or before opening (e.g. 05:00–02:00), the
  // closing time is interpreted as the NEXT day → add 24h. Round-the-clock
  // (00:00–00:00) then gives closeMin = 1440 (the whole day).
  if (closeMin <= openMin) closeMin += 1440;
  const dayBookings = (bookings || []).filter((b) => b.date === isoDate);
  const courtList =
    enabledCourts && enabledCourts.length > 0
      ? enabledCourts
      : Array.from({ length: totalCourts }, (_, i) => i + 1);
  const slots: any[] = [];
  // For each time (step of stepMin), check which courts are free. Smart booking
  // applies here the SAME way as in the court picker: a court with more slack
  // than another free court is locked, so the slot list and the picker always
  // show the same availability.
  for (let t = openMin; t + durationMin <= closeMin; t += stepMin) {
    const startTime = fromMinHHMM(t);
    const endTime = fromMinHHMM(t + durationMin);
    const freeBaneList: any[] = []; // smart-approved courts (bookable now)
    const physicallyFree: any[] = []; // gap-legal courts (actually free, regardless of smart)
    for (const court of courtList) {
      const candidate = { court, date: isoDate, startTime, endTime };
      // Smart validation (full): controls which court is recommended
      const v = validateBooking(candidate, dayBookings, {
        bufferMin,
        minBookingMin,
        smartAllocation,
        totalCourts,
        enabledCourts,
        allowedDurations,
      });
      if (v.ok) freeBaneList.push(court);
      // Gap validation (without smart): counts actually free courts. This is what
      // the user should see — "3 of 5 free" reflects what is physically open,
      // even though smart booking steers you toward one specific court.
      const gapV = validateBooking(candidate, dayBookings, {
        bufferMin,
        minBookingMin,
        smartAllocation: false,
      });
      if (gapV.ok) physicallyFree.push(court);
    }
    // A slot is bookable if at least one court is smart-approved. We keep both
    // lists: freeBaneList (bookable now) and physicallyFree (the counter).
    if (freeBaneList.length > 0) {
      slots.push({
        startTime,
        endTime,
        freeCourts: physicallyFree.length, // ← counts physically free
        bookableCourts: freeBaneList.length, // smart-approved (default court)
        totalCourts: courtList.length,
        freeBaneList,
        physicallyFreeList: physicallyFree,
      });
    }
  }
  return slots;
}

// ------------------------------------------------------------
// Recurrence expansion (Outlook-style rule -> ISO date list). Relocated from
// legacy/App.jsx so both the booking calendar and the seriespill slot engine can
// share it; it now lives here in src/engine instead. Pure date arithmetic.
// ------------------------------------------------------------
// Expand an Outlook-style recurrence rule into a list of ISO dates.
// recur: { freq: daily|weekly|monthly|yearly, interval, weekdays:[0..6 mon=0],
//          endType: count|until|never, count, until }
// startIso = first date. Returns sorted ISO dates (max 366 for safety).
export function expandRecurrence(startIso, recur) {
  const MAX = 366; // hard safety limit
  const out = [];
  const start = new Date(startIso + 'T00:00:00');
  const toIso = (d) => {
    const y = d.getFullYear(),
      m = String(d.getMonth() + 1).padStart(2, '0'),
      da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  };
  const interval = Math.max(1, recur.interval || 1);
  const endType = recur.endType || 'count';
  const count = Math.max(1, recur.count || 1);
  const until = recur.until ? new Date(recur.until + 'T00:00:00') : null;
  const reached = (n, d) => {
    if (endType === 'count') return n >= count;
    if (endType === 'until') return until && d > until;
    return n >= MAX; // 'never' — bounded by MAX
  };

  if (recur.freq === 'weekly') {
    // Weekdays (mon=0..sun=6). Default: the weekday of the start date.
    const wds =
      recur.weekdays && recur.weekdays.length > 0
        ? [...recur.weekdays].sort((a, b) => a - b)
        : [(start.getDay() + 6) % 7];
    // Go week by week (interval), including the chosen weekdays in each active week.
    // Find the Monday of the start week.
    const startWd = (start.getDay() + 6) % 7;
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() - startWd);
    let weekIdx = 0;
    while (out.length < MAX) {
      if (weekIdx % interval === 0) {
        for (const wd of wds) {
          const d = new Date(weekStart);
          d.setDate(weekStart.getDate() + weekIdx * 7 + wd);
          if (d < start) continue; // not before the start date
          if (reached(out.length, d)) return out.sort();
          out.push(toIso(d));
        }
      }
      weekIdx++;
      if (weekIdx > MAX) break;
    }
  } else if (recur.freq === 'daily') {
    let step = 0;
    while (out.length < MAX) {
      const d = new Date(start);
      d.setDate(start.getDate() + step * interval);
      if (reached(out.length, d)) break;
      out.push(toIso(d));
      step++;
      if (step > MAX) break;
    }
  } else if (recur.freq === 'monthly') {
    let step = 0;
    while (out.length < MAX) {
      const d = new Date(start);
      d.setMonth(start.getMonth() + step * interval);
      if (reached(out.length, d)) break;
      out.push(toIso(d));
      step++;
      if (step > MAX) break;
    }
  } else if (recur.freq === 'yearly') {
    let step = 0;
    while (out.length < MAX) {
      const d = new Date(start);
      d.setFullYear(start.getFullYear() + step * interval);
      if (reached(out.length, d)) break;
      out.push(toIso(d));
      step++;
      if (step > 100) break;
    }
  }
  return out.sort();
}
